import { isApiClientError, isValidationApiError } from "../api/errors";
import {
  failureStatusValues,
  type ImportFailuresQuery,
} from "../entities/mailboxes";

export type MailboxesTab = "mailboxes" | "failures";

export const mailboxesTabs: readonly MailboxesTab[] = ["mailboxes", "failures"];

export const newMailboxId = "new";

export type EnabledFilter = "all" | "enabled" | "disabled";
export type RetryableFilter = "all" | "yes" | "no";

export interface MailboxesSearch {
  tab: MailboxesTab;
  /** Открытый ящик или `new` для формы создания. */
  id?: string;
  /** Фильтр «включён / отключён» в списке ящиков. */
  enabled: EnabledFilter;
  /** Открытая ошибка импорта. */
  failure?: string;
  /** Фильтры журнала ошибок. */
  status: string;
  retryable: RetryableFilter;
  stage: string;
  mailbox: string;
}

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Значения из адресной строки недоверенные: всё, что не подошло, отбрасывается. */
export function validateMailboxesSearch(
  value: Record<string, unknown>,
): MailboxesSearch {
  const text = (candidate: unknown): string | undefined =>
    typeof candidate === "string" && candidate.trim() !== ""
      ? candidate.trim().slice(0, 64)
      : undefined;
  const id = text(value.id);
  const failure = text(value.failure);
  const mailbox = text(value.mailbox);
  const status = text(value.status);
  const stage = text(value.stage);
  return {
    tab: mailboxesTabs.find((item) => item === value.tab) ?? "mailboxes",
    enabled:
      value.enabled === "enabled" || value.enabled === "disabled"
        ? value.enabled
        : "all",
    status:
      status !== undefined &&
      (failureStatusValues as readonly string[]).includes(status)
        ? status
        : "",
    retryable:
      value.retryable === "yes" || value.retryable === "no"
        ? value.retryable
        : "all",
    stage: stage ?? "",
    mailbox: mailbox !== undefined && uuidPattern.test(mailbox) ? mailbox : "",
    ...(id === undefined ? {} : { id }),
    ...(failure === undefined ? {} : { failure }),
  };
}

/** Из адресной строки убираются значения по умолчанию: ссылка остаётся короткой. */
export function compactMailboxesSearch(
  search: MailboxesSearch,
): Partial<MailboxesSearch> {
  return {
    ...(search.tab === "mailboxes" ? {} : { tab: search.tab }),
    ...(search.id === undefined ? {} : { id: search.id }),
    ...(search.enabled === "all" ? {} : { enabled: search.enabled }),
    ...(search.failure === undefined ? {} : { failure: search.failure }),
    ...(search.status === "" ? {} : { status: search.status }),
    ...(search.retryable === "all" ? {} : { retryable: search.retryable }),
    ...(search.stage === "" ? {} : { stage: search.stage }),
    ...(search.mailbox === "" ? {} : { mailbox: search.mailbox }),
  };
}

/**
 * Проблема с зависимостью, а не с данными человека. 429 и 503 — разные
 * состояния: при первом нужно подождать, при втором сервис или почтовый сервер
 * не отвечает, и ждать надо восстановления.
 */
export type DependencyIssue =
  | { kind: "rate-limited"; retryAfterSeconds: number | null }
  | {
      kind: "unavailable";
      /**
       * Почему недоступно: сам ящик (Core не смог получить к нему доступ — чаще
       * всего ссылка на секрет не находится на сервере) или сервис целиком.
       */
      source: "mailbox" | "service";
      retryAfterSeconds: number | null;
    };

function retryAfterSeconds(error: {
  responseHeaders: { retryAfter: string | null };
}): number | null {
  const raw = error.responseHeaders.retryAfter;
  if (raw === null) return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
  const date = Date.parse(raw);
  if (Number.isNaN(date)) return null;
  return Math.max(0, Math.ceil((date - Date.now()) / 1000));
}

const rateLimitedCodes = new Set([
  "request.rate_limited",
  "gateway.rate_limited",
]);
const unavailableCodes = new Set([
  "dependency.unavailable",
  "gateway.upstream_unavailable",
  "gateway.upstream_not_ready",
  "document_workflow.mailbox_unavailable",
]);

export function dependencyIssue(error: unknown): DependencyIssue | null {
  if (!isApiClientError(error)) return null;
  if (error.status === 429 || rateLimitedCodes.has(error.code)) {
    return {
      kind: "rate-limited",
      retryAfterSeconds: retryAfterSeconds(error),
    };
  }
  if (error.status === 503 || unavailableCodes.has(error.code)) {
    return {
      kind: "unavailable",
      source:
        error.code === "document_workflow.mailbox_unavailable"
          ? "mailbox"
          : "service",
      retryAfterSeconds: retryAfterSeconds(error),
    };
  }
  return null;
}

/** Текст ошибки, которая не про зависимость: права, конфликт, валидация. */
export function mailboxErrorText(error: unknown, fallback: string): string {
  if (isValidationApiError(error)) {
    return "Сервер не принял данные. Проверьте отмеченные поля.";
  }
  if (isApiClientError(error)) {
    if (error.status === 401) return "Сессия истекла. Войдите снова.";
    if (error.status === 403) return "Недостаточно прав для этого действия.";
    if (error.status === 404)
      return "Запись не найдена: возможно, она уже недоступна. Обновите список.";
    if (error.status === 409)
      return "Действие сейчас недоступно: состояние записи изменилось. Обновите страницу и проверьте, что выбрана конкретная организация.";
    if (error.code === "document_workflow.module_disabled")
      return "Модуль документооборота отключён.";
    if (error.code === "document_workflow.idempotency_key_required")
      return "Запрос не принят без ключа повтора. Обновите страницу и повторите.";
  }
  return fallback;
}

export function describeSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} с`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} мин`;
}

/** Ключи кэша: почтовые ящики и журнал ошибок инвалидируются вместе с ящиком. */
export const mailboxKeys = {
  all: ["mailboxes"] as const,
  list: (enabled: EnabledFilter) => ["mailboxes", "list", enabled] as const,
  detail: (id: string) => ["mailboxes", "detail", id] as const,
  syncState: (id: string) => ["mailboxes", "sync-state", id] as const,
  failures: ["mailboxes", "failures"] as const,
  failureList: (filters: object) =>
    ["mailboxes", "failures", "list", filters] as const,
  failure: (id: string) => ["mailboxes", "failures", "detail", id] as const,
};

/** Параметры запроса Core из фильтров адресной строки. */
export function failuresQuery(search: MailboxesSearch): ImportFailuresQuery {
  return {
    ...(search.status === "" ? {} : { status: search.status }),
    ...(search.stage === "" ? {} : { stage: search.stage }),
    ...(search.retryable === "all"
      ? {}
      : { retryable: search.retryable === "yes" }),
    ...(search.mailbox === "" ? {} : { mailbox_id: search.mailbox }),
  };
}

/** Название организации (КПО) по идентификатору; `null`, если организация текущему пользователю недоступна. */
export type OrganizationName = (id: string) => string | null;

/** Ящик с организацией: в режиме «Все организации» одного названия ящика мало. */
export function mailboxLabel(
  mailbox: { name: string; access_scope_id: string },
  organizationName: OrganizationName,
): string {
  const organization = organizationName(mailbox.access_scope_id);
  return organization === null
    ? mailbox.name
    : `${mailbox.name} · ${organization}`;
}
